import React, { useState, useRef, useEffect } from 'react';
import { Eye, Code, FileCode2, Plus, Minus, FileText, Trash2, Upload, Download, Moon, Sun, Shield, Info, Sparkles, X, Globe, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import { translations, getTranslation } from '../i18n/translations';
import axios from 'axios';
import SliderCarousel from './SliderCarousel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HtmlViewer = ({ onAdminClick }) => {
  const [htmlCode, setHtmlCode] = useState('Put your HTML text here');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [isGenerating, setIsGenerating] = useState(false);
  const [language, setLanguage] = useState('en');
  const [generateImage, setGenerateImage] = useState(false);
  const [imageProvider, setImageProvider] = useState('openai');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [useTranslation, setUseTranslation] = useState(false);
  const [turkishInput, setTurkishInput] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);
  const iframeRef = useRef(null);
  const fileInputRef = useRef(null);

  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    updatePreview();
  }, [htmlCode]);

  const updatePreview = () => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlCode);
      iframeDoc.close();
    }
  };

  const handleFormat = () => {
    try {
      const formatted = formatHtml(htmlCode);
      setHtmlCode(formatted);
      toast({
        title: t('formatted'),
        description: t('formattedDesc'),
      });
    } catch (e) {
      toast({
        title: "Error",
        description: "Could not format HTML",
        variant: "destructive",
      });
    }
  };

  const formatHtml = (html) => {
    let formatted = '';
    let indent = 0;
    const tab = '  ';
    
    html.split(/(<[^>]+>)/g).forEach(node => {
      if (node.match(/^<\/\w/)) {
        indent--;
      }
      if (node.trim()) {
        formatted += tab.repeat(Math.max(0, indent)) + node.trim() + '\n';
      }
      if (node.match(/^<\w[^>]*[^\/]>$/)) {
        indent++;
      }
    });
    
    return formatted.trim();
  };

  const handleExpandAll = () => {
    toast({
      title: t('expanded'),
      description: t('expandedDesc'),
    });
  };

  const handleCollapseAll = () => {
    toast({
      title: t('collapsed'),
      description: t('collapsedDesc'),
    });
  };

  const handleSample = () => {
    const sampleHtml = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Sample HTML</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            padding: 20px;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
        }
        .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
            color: #333;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 10px 30px rgba(0,0,0,0.3);
        }
        h1 {
            color: #667eea;
            border-bottom: 3px solid #667eea;
            padding-bottom: 10px;
        }
        .button {
            background: #667eea;
            color: white;
            padding: 10px 20px;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            margin-top: 20px;
        }
    </style>
</head>
<body>
    <div class="container">
        <h1>Welcome to HTML Online Viewer</h1>
        <p>This is a sample HTML document with styling.</p>
        <ul>
            <li>Real-time preview</li>
            <li>HTML formatting</li>
            <li>Import/Export functionality</li>
        </ul>
        <button class="button" onclick="alert('Hello World!')">Click Me</button>
    </div>
</body>
</html>`;
    setHtmlCode(sampleHtml);
  };

  const handleClear = () => {
    setHtmlCode('');
  };

  const handleImport = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setHtmlCode(event.target?.result || '');
        toast({
          title: t('imported'),
          description: `${t('importedDesc')} "${file.name}"`,
        });
      };
      reader.readAsText(file);
    }
  };

  const handleExport = () => {
    const blob = new Blob([htmlCode], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'exported.html';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: t('exported'),
      description: t('exportedDesc'),
    });
  };

  const handlePreview = () => {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(htmlCode);
      newWindow.document.close();
    }
  };

  const handleHighlight = () => {
    toast({
      title: "Highlight",
      description: "Syntax highlighting enabled",
    });
  };

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    toast({
      title: isDarkMode ? "Light mode" : "Dark mode",
      description: `Switched to ${isDarkMode ? 'light' : 'dark'} mode`,
    });
  };

  const handleTranslate = async () => {
    if (!turkishInput.trim()) {
      toast({
        title: "Error",
        description: "Lütfen Türkçe açıklama girin",
        variant: "destructive",
      });
      return;
    }

    setIsTranslating(true);
    try {
      const response = await axios.post(`${API}/translate-prompt`, {
        turkish_text: turkishInput
      });

      setAiPrompt(response.data.english_prompt);
      toast({
        title: "Çevrildi!",
        description: "Türkçe açıklamanız İngilizce'ye çevrildi",
      });
    } catch (error) {
      console.error('Translation error:', error);
      toast({
        title: "Çeviri Hatası",
        description: "Çeviri yapılamadı, lütfen tekrar deneyin",
        variant: "destructive",
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast({
        title: "Error",
        description: "Please enter a prompt",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);
    try {
      const response = await axios.post(`${API}/generate-html`, {
        prompt: aiPrompt,
        provider: selectedProvider,
        language: language,
        generate_image: generateImage,
        image_provider: imageProvider
      });

      setHtmlCode(response.data.html_code);
      
      if (response.data.image_base64) {
        setGeneratedImage(`data:image/png;base64,${response.data.image_base64}`);
      }
      
      setShowAIModal(false);
      setAiPrompt('');
      setGenerateImage(false);
      setGeneratedImage(null);
      
      toast({
        title: t('generatedSuccess'),
        description: generateImage 
          ? `HTML code and image generated with ${response.data.image_provider === 'openai' ? 'DALL-E' : 'Gemini Nano Banana'}!`
          : t('generatedSuccessDesc'),
      });
    } catch (error) {
      console.error('AI generation error:', error);
      toast({
        title: t('generationError'),
        description: error.response?.data?.detail || 'Failed to generate HTML',
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".html,.htm"
        className="hidden"
      />
      
      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar */}
        <div className={`w-[160px] flex-shrink-0 ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-[#2d2d2d]'} text-white flex flex-col`}>
          <div className="p-4 text-sm font-semibold border-b border-gray-700">
            {t('title')}
          </div>

          {/* Language Selector */}
          <div className="px-3 py-2 border-b border-gray-700">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="w-3 h-3 text-gray-400" />
              <span className="text-xs text-gray-400">{t('language')}</span>
            </div>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="w-full bg-gray-700 text-white text-xs px-2 py-1 rounded border border-gray-600 hover:bg-gray-600 transition-colors"
            >
              <option value="en">English</option>
              <option value="tr">Türkçe</option>
            </select>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            <SidebarButton icon={Eye} label={t('preview')} onClick={handlePreview} />
            <SidebarButton icon={Code} label={t('highlight')} onClick={handleHighlight} />
            <SidebarButton icon={FileCode2} label={t('format')} onClick={handleFormat} />
            <SidebarButton icon={Plus} label={t('expandAll')} onClick={handleExpandAll} />
            <SidebarButton icon={Minus} label={t('collapseAll')} onClick={handleCollapseAll} />
            <SidebarButton icon={FileText} label={t('sample')} onClick={handleSample} />
            <SidebarButton icon={Trash2} label={t('clear')} onClick={handleClear} />
            <SidebarButton icon={Upload} label={t('importFile')} onClick={handleImport} />
            <SidebarButton icon={Download} label={t('exportFile')} onClick={handleExport} />
            <SidebarButton 
              icon={isDarkMode ? Sun : Moon} 
              label={t('darkMode')} 
              onClick={toggleDarkMode} 
            />
          </div>

          <div className="border-t border-gray-700">
            <SidebarButton icon={Shield} label={t('privacySettings')} onClick={() => setShowPrivacy(true)} />
            <SidebarButton icon={Info} label={t('about')} onClick={() => setShowAbout(true)} />
            {onAdminClick && (
              <SidebarButton icon={Settings} label="Admin Panel" onClick={onAdminClick} />
            )}
          </div>
        </div>

        {/* Middle - Code Editor */}
        <div className={`flex-1 flex flex-col ${isDarkMode ? 'bg-[#1e1e1e]' : 'bg-[#f5f5f5]'}`}>
          <div className={`flex items-center gap-2 px-4 py-2 border-b ${isDarkMode ? 'bg-[#252526] border-gray-700' : 'bg-white border-gray-300'}`}>
            <FileCode2 className="w-4 h-4" />
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>1</span>
            <span className={`text-sm ${isDarkMode ? 'text-blue-400' : 'text-blue-600'} ml-2`}>
              {htmlCode.slice(0, 50)}{htmlCode.length > 50 ? '...' : ''}
            </span>
          </div>
          <textarea
            value={htmlCode}
            onChange={(e) => setHtmlCode(e.target.value)}
            className={`flex-1 w-full p-4 font-mono text-sm resize-none outline-none ${
              isDarkMode 
                ? 'bg-[#1e1e1e] text-gray-300' 
                : 'bg-white text-gray-800'
            }`}
            style={{
              lineHeight: '1.6',
              tabSize: 2,
            }}
            spellCheck={false}
          />
        </div>

        {/* Right - Preview */}
        <div className="flex-1 border-l-2 border-gray-300 bg-white overflow-hidden">
          <iframe
            ref={iframeRef}
            className="w-full h-full border-none"
            title="HTML Preview"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      </div>

      {/* Slider Section (replacing Discover More) */}
      <SliderCarousel language={language} />

      {/* Floating AI Assistant Button */}
      <button
        onClick={() => setShowAIModal(true)}
        className="fixed bottom-8 right-8 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-50 flex items-center gap-2 group"
      >
        <Sparkles className="w-6 h-6" />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 whitespace-nowrap">
          {t('aiAssistant')}
        </span>
      </button>

      {/* AI Modal */}
      {showAIModal && (
        <Modal title={t('aiAssistant')} onClose={() => setShowAIModal(false)} wide>
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
              <p className="text-sm text-gray-700">{t('aiHelper')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Select AI Provider for HTML:</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setSelectedProvider('openai')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedProvider === 'openai'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-sm">OpenAI</div>
                  <div className="text-xs text-gray-500">GPT-5.2</div>
                </button>
                <button
                  onClick={() => setSelectedProvider('anthropic')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedProvider === 'anthropic'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-sm">Claude</div>
                  <div className="text-xs text-gray-500">Sonnet 4.5</div>
                </button>
                <button
                  onClick={() => setSelectedProvider('gemini')}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedProvider === 'gemini'
                      ? 'border-purple-600 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-sm">Gemini</div>
                  <div className="text-xs text-gray-500">Flash 3</div>
                </button>
              </div>
            </div>

            {/* Image Generation Checkbox */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="generate-image"
                  checked={generateImage}
                  onChange={(e) => setGenerateImage(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="generate-image" className="text-sm font-medium cursor-pointer">
                  🎨 Görsel de oluştur (Generate Image)
                </label>
              </div>

              {generateImage && (
                <div className="ml-6 space-y-2 animate-fade-in">
                  <label className="block text-sm font-medium">Select Image AI Provider:</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setImageProvider('openai')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        imageProvider === 'openai'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-sm">DALL-E</div>
                      <div className="text-xs text-gray-500">OpenAI Image</div>
                    </button>
                    <button
                      onClick={() => setImageProvider('gemini')}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        imageProvider === 'gemini'
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-sm">Nano Banana 🍌</div>
                      <div className="text-xs text-gray-500">Gemini Image</div>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    ℹ️ Image will be embedded into the generated HTML code
                  </p>
                </div>
              )}
            </div>

            {/* Turkish to English Translation */}
            <div className="border-t pt-4">
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="checkbox"
                  id="use-translation"
                  checked={useTranslation}
                  onChange={(e) => setUseTranslation(e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <label htmlFor="use-translation" className="text-sm font-medium cursor-pointer">
                  🇹🇷→🇬🇧 Türkçe'den İngilizce'ye çevir (Better AI results)
                </label>
              </div>

              {useTranslation && (
                <div className="space-y-3 animate-fade-in">
                  <div>
                    <label className="block text-sm font-medium mb-1">Türkçe Açıklama:</label>
                    <textarea
                      value={turkishInput}
                      onChange={(e) => setTurkishInput(e.target.value)}
                      placeholder="Türkçe olarak ne istediğinizi açıklayın... (örn: 'Mavi bir buton ve başlık oluştur')"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent resize-none"
                      rows={3}
                    />
                  </div>
                  <Button
                    onClick={handleTranslate}
                    disabled={isTranslating || !turkishInput.trim()}
                    variant="outline"
                    className="w-full"
                  >
                    {isTranslating ? (
                      <>
                        <div className="animate-spin mr-2 h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full" />
                        Çevriliyor...
                      </>
                    ) : (
                      <>
                        🔄 İngilizce'ye Çevir
                      </>
                    )}
                  </Button>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>↓</span>
                    <span>İngilizce prompt aşağıda görünecek</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {useTranslation ? 'English Prompt (Translated):' : 'Prompt:'}
              </label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={t('aiPromptPlaceholder')}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent resize-none"
                rows={4}
                disabled={useTranslation && isTranslating}
              />
              {useTranslation && (
                <p className="text-xs text-gray-500 mt-1">
                  ℹ️ Bu prompt AI tarafından kullanılacak (manuel düzenleyebilirsiniz)
                </p>
              )}
            </div>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowAIModal(false)}
                disabled={isGenerating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAIGenerate}
                disabled={isGenerating || !aiPrompt.trim()}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              >
                {isGenerating ? (
                  <>
                    <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                    {t('generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('generate')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* About Modal */}
      {showAbout && (
        <Modal title={t('aboutTitle')} onClose={() => setShowAbout(false)}>
          <div className="space-y-4">
            <p className="text-sm">{t('aboutDescription')}</p>
            <p className="text-sm">{t('aboutDescription2')}</p>
            <p className="text-sm text-gray-500">Version 1.0.0</p>
          </div>
        </Modal>
      )}

      {/* Privacy Modal */}
      {showPrivacy && (
        <Modal title={t('privacyTitle')} onClose={() => setShowPrivacy(false)}>
          <div className="space-y-4">
            <p className="text-sm">{t('privacyDescription')}</p>
            <p className="text-sm">{t('privacyDescription2')}</p>
          </div>
        </Modal>
      )}
    </div>
  );
};

const SidebarButton = ({ icon: Icon, label, onClick }) => {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-700 transition-colors flex items-center gap-2 text-gray-300 hover:text-white"
    >
      <Icon className="w-4 h-4" />
      <span>{label}</span>
    </button>
  );
};

const Modal = ({ title, children, onClose, wide = false }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className={`bg-white rounded-lg p-6 ${wide ? 'max-w-2xl' : 'max-w-md'} w-full mx-4`} onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default HtmlViewer;
